import qrcode from 'qrcode-generator';

export interface QrOptions {
    size?: number;
    margin?: number;
    typeNumber?: number; // 0 = auto
    errorCorrectionLevel?: 'L' | 'M' | 'Q' | 'H';
}

/**
 * Genera un QR code como Data URL (PNG base64)
 * @param text Texto a encodear (ej: https://link.caloo.app/r/CODE)
 * @param options Opciones de generación
 * @returns Promise<string> Data URL del QR
 */
export async function generateQrDataUrl(
    text: string,
    options: QrOptions = {}
): Promise<string> {
    const {
        size = 512,
        margin = 4,
        typeNumber = 0,
        errorCorrectionLevel = 'M'
    } = options;

    return new Promise((resolve, reject) => {
        try {
            // Crear QR con qrcode-generator
            const qr = qrcode(typeNumber, errorCorrectionLevel);
            qr.addData(text);
            qr.make();

            // Obtener tamaño de módulos
            const moduleCount = qr.getModuleCount();
            const cellSize = Math.floor((size - margin * 2) / moduleCount);
            const actualSize = cellSize * moduleCount + margin * 2;

            // Crear canvas
            const canvas = document.createElement('canvas');
            canvas.width = actualSize;
            canvas.height = actualSize;
            const ctx = canvas.getContext('2d');

            if (!ctx) {
                reject(new Error('No se pudo obtener contexto 2D del canvas'));
                return;
            }

            // Fondo blanco
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, actualSize, actualSize);

            // Dibujar módulos del QR
            ctx.fillStyle = '#000000';
            for (let row = 0; row < moduleCount; row++) {
                for (let col = 0; col < moduleCount; col++) {
                    if (qr.isDark(row, col)) {
                        ctx.fillRect(
                            col * cellSize + margin,
                            row * cellSize + margin,
                            cellSize,
                            cellSize
                        );
                    }
                }
            }

            // Exportar como Data URL
            resolve(canvas.toDataURL('image/png'));
        } catch (error) {
            reject(error);
        }
    });
}

/**
 * Genera un QR y lo devuelve como HTMLImageElement cargado
 */
export async function generateQrImage(
    text: string,
    options: QrOptions = {}
): Promise<HTMLImageElement> {
    const dataUrl = await generateQrDataUrl(text, options);
    return loadImageFromDataUrl(dataUrl);
}

/**
 * Helper: carga imagen desde data URL
 */
function loadImageFromDataUrl(dataUrl: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = dataUrl;
    });
}
